<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	// Stores
	import { app } from 'src/stores/session/native-bridge';
	// Services
	import { format } from '../../services/i18n';
	// Helpers
	import { capFirstChar } from '../../helpers/utilities';
	import { navigateToUrl } from '../Lists/list-helpers';
	//Design system components
	import { Button, Text, Modal, Container, Icon } from '@Shelly/shelly-design-system';
	// Components
	import EmptyList from './EmptyList.svelte';
	import { includeDeviceModal } from 'src/stores/features/inclusion';
	import {
		deviceMainFilter,
		devicesFilters,
		getSelectedDeviceFilterCount,
	} from 'src/stores/ui/filters';
	import NoFilterSearchResults from './Devices/NoFilterSearchResults.svelte';
	import BleWebInclusionModal from '../BleWebInclusion/BleWebInclusionModal.svelte';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	import SharedHomeOwnerManagedModal from 'src/components/SharedHome/SharedHomeOwnerManagedModal.svelte';

	export let showModal = false;
	export let hideMessage = false;
	export let onlySharedDevices = false; //this is needed to change title and description for activity log if shared devices only
	export let hideIcon = false;

	let showSharedManagedModal = false;

	$: showAddDeviceButton = !onlySharedDevices;

	$: title = onlySharedDevices
		? $format('activity_log_only_shared_title', {
				default: `Your account doesn't contain valid activity log devices`,
			})
		: $format('on_board_add_device_title', {
				default: 'No devices in this room.',
			});
	$: description = onlySharedDevices
		? $format('activity_log_add_device_shared_info', {
				default: 'Please add a new device in order to register events.',
			})
		: $format('on_board_add_device_info', {
				default: 'You can add a new device or move one from another room.',
			});

	const shellyCoudAppLink = 'https://www.shelly.cloud/app_download';
	const shellyCoudQRCodePath = '/qrcodes/shelly-cloud-app.svg';

	const appStoreData = {
		qrCodePath: shellyCoudQRCodePath,
		appStoreLink: shellyCoudAppLink,
	};

	const onAddButtonClick = () => {
		if (isActiveHomeShared($homes.list)) {
			showSharedManagedModal = true;
			return;
		}
		showModal = true;
		includeDeviceModal.update((state) => {
			// $app returns true if the app is running in the mobile app
			return (state = { ...state, show: $app });
		});
	};

	const onCloseModalOpenApp = () => {
		showModal = false;
	};

	// handle global modals escape key
	onMount(() => {
		document.addEventListener('keydown', handleKeydown);
	});

	onDestroy(() => {
		document.removeEventListener('keydown', handleKeydown);
	});

	const handleKeydown = (e: KeyboardEvent) => {
		if (showModal && e.key == 'Escape') {
			showModal = false;
		}
	};

	let bleWebInclusionModal = false;
</script>

{#if !hideMessage}
	{#if $deviceMainFilter || getSelectedDeviceFilterCount($devicesFilters)}
		<NoFilterSearchResults marginTop="l" marginTopMd="l" marginTopSm="s" />
	{:else}
		<div class="mt-md-m mt-sm-0 text-center">
			{#if !onlySharedDevices && !hideIcon}
				<Icon
					size="l"
					class="text-white mt-md-l mt-sm-s mb-m"
					icon="fak fa-light-microchip-slash"
				/>
			{/if}
			<EmptyList
				{title}
				{description}
				withoutMargin={true}
				btnText={showAddDeviceButton
					? capFirstChar(
							$format('button_add_device', {
								default: 'Add device',
							}),
						)
					: null}
				onClick={() => {
					onAddButtonClick();
				}}
			/>
		</div>
	{/if}
{/if}

{#if !$app}
	<Modal
		on:close={onCloseModalOpenApp}
		{showModal}
		title={$format('on_board_app_required_title', {
			default: 'Mobile app required',
		})}
	>
		<Container slot="content" padding="0">
			<Text class="text-bold text-center mb-s">
				{$format('on_board_steps_title', {
					default: 'You need Shelly App for adding a device.',
				})}
			</Text>

			<Text class="text-center">
				{$format('on_board_step_one', {
					default: '1. Click the Button or Scan the QR code to Install our app.',
				})}
			</Text>
			<Text class="text-center mb-l">
				{$format('on_board_step_two', {
					default: '2. Start Adding Devices and enjoy app’s full potential.',
				})}
			</Text>

			<div class="d-flex flex-direction-column align-items-center justify-content-center">
				<img
					width="146"
					height="146"
					src={appStoreData.qrCodePath}
					alt={$format('on_board_qr_code', {
						default: 'QR code',
					})}
					class="mb-l"
				/>

				<Button
					class="width-md-auto width-sm-full mb-m"
					title={$format('button_install_app', {
						default: 'Install App',
					})}
					on:click={() => {
						navigateToUrl(appStoreData.appStoreLink);
					}}
				>
					{$format('button_install_app', {
						default: 'Install App',
					})}
				</Button>

				<Text class="text-center mb-m" element="h2">
					{$format('text_on_board_web_bluetooth_info_or', {
						default: 'Or',
					})}
				</Text>
				<Text class="text-center mb-m">
					{$format('text_on_board_web_bluetooth_info', {
						default: 'Try out the Web Bluetooth inclusion.',
					})}
				</Text>
				<Button
					class="width-md-auto width-sm-full mb-m"
					variant="secondary"
					on:click={() => {
						bleWebInclusionModal = true;
						onCloseModalOpenApp();
					}}
				>
					{$format('button_include_device_via_web_bluetooth', {
						default: 'Include Device via Web Bluetooth',
					})}
				</Button>

				<!-- svelte-ignore a11y-click-events-have-key-events -->
				<!-- svelte-ignore a11y-no-static-element-interactions -->
				<div
					title={$format('button_later', {
						default: 'Later',
					})}
					class="link-styles"
					on:click={onCloseModalOpenApp}
				>
					{$format('button_later', {
						default: 'Later',
					})}
				</div>
			</div>
		</Container>
	</Modal>
{/if}

{#if bleWebInclusionModal}
	<BleWebInclusionModal bind:showModal={bleWebInclusionModal} />
{/if}

<SharedHomeOwnerManagedModal bind:showModal={showSharedManagedModal} context="add_device" />
